import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { MagnifyingGlass, Plus, Storefront, CaretRight } from "@phosphor-icons/react";
import { TopHeader } from "@/components/TopHeader";

export default function CustomersPage() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCustomers();
  }, [search]);

  const fetchCustomers = async () => {
    try {
      const params = search ? { search } : {};
      const response = await axios.get(`${API}/customers`, { params });
      setCustomers(response.data);
    } catch (error) {
      toast.error("Failed to load customers");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="customers-page">
      {/* Header */}
      <TopHeader title="Customers" subtitle="CRM">
        <Button
          onClick={() => navigate("/customers/new")}
          className="h-10 px-4 bg-[#0055FF] hover:bg-[#0040CC] text-white font-semibold rounded-full"
          data-testid="add-customer-button"
        >
          <Plus size={20} weight="bold" className="mr-1" />
          Add
        </Button>
      </TopHeader>

      {/* Search */}
      <div className="px-4 pt-4">
        <div className="search-bar">
          <MagnifyingGlass size={20} className="text-[#9CA3AF] mr-2" />
          <input
            type="text"
            value={search}
            onChange={handleSearch}
            placeholder="Search by company, name or notes..."
            data-testid="customer-search-input"
          />
        </div>
      </div>

      {/* Customer List */}
      <div className="p-4">
        {loading ? (
          <div className="flex items-center justify-center py-12" data-testid="customers-loading">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0055FF]"></div>
          </div>
        ) : customers.length > 0 ? (
          <div className="card-flat overflow-hidden">
            {customers.map((customer, index) => (
              <div
                key={customer.id}
                className="list-item animate-fade-in-up"
                style={{ animationDelay: `${index * 0.05}s` }}
                onClick={() => navigate(`/customers/${customer.id}`)}
                data-testid={`customer-item-${index}`}
              >
                <div className="w-12 h-12 rounded-lg bg-[#F3F4F6] flex items-center justify-center mr-3 flex-shrink-0">
                  <Storefront size={24} className="text-[#4B5563]" weight="fill" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-[#0A0A0A] truncate">
                    {customer.company_name || customer.name}
                  </p>
                  <p className="text-sm text-[#4B5563] truncate">
                    {customer.company_name ? customer.name : (customer.phone || customer.email || "No contact")}
                    {customer.company_name && customer.phone && ` • ${customer.phone}`}
                  </p>
                  {customer.business_type && (
                    <span className="inline-block mt-1 text-xs px-2 py-0.5 bg-[#F3F4F6] rounded text-[#4B5563]">
                      {customer.business_type}
                    </span>
                  )}
                </div>
                <CaretRight size={20} className="text-[#9CA3AF] flex-shrink-0" />
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state" data-testid="customers-empty">
            <div className="empty-state-icon">
              <Storefront size={48} />
            </div>
            <h3 className="empty-state-title">No Customers Yet</h3>
            <p className="empty-state-text">
              {search
                ? "No customers match your search"
                : "Add your first customer to get started"}
            </p>
            {!search && (
              <Button
                onClick={() => navigate("/customers/new")}
                className="mt-4 bg-[#0055FF] hover:bg-[#0040CC] text-white font-semibold rounded-full"
                data-testid="add-first-customer-button"
              >
                <Plus size={20} weight="bold" className="mr-1" />
                Add Customer
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
